import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core'
import { useIntl } from 'react-intl'
/** Chart rendering via https://www.chartjs.org/ and react-chartjs-2 */
import { Bar, Line, Pie } from 'react-chartjs-2'

import {
  AccidentAtlasChartConfig,
  isCartesianChartConfig,
  isPieChartConfig,
} from '../types/api'
import { AccidentAtlasChartRenderType } from '../types/tile'
import { getChartColor } from '../lib/chartColors'
import {
  chartToBarChartJsConfig,
  chartToLineChartJsConfig,
  chartToPieChartJsConfig,
} from '../lib/chartToChartJsConfig'
import {
  buildCartesianDataTable,
  buildCartesianSummaryText,
  buildPieDataTable,
} from '../lib/buildChartAccessibility'
import {
  CARTESIAN_CHART_PAGE_SIZE,
  getCartesianChartMax,
  sliceCartesianChart,
} from '../lib/sliceCartesianChart'
import { registerChartJs } from '@tools/registerChartJs'
import ChartDataTable from './chartDataTable'
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion'

registerChartJs()

const useStyles = makeStyles((theme) => ({
  figureRoot: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    margin: 0,
  },
  visuallyHidden: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  root: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
  },
  pieRoot: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(12),
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
  },
  pieChart: {
    position: 'relative',
    flex: '1 0 33%',
    minWidth: 0,
    height: '100%',
    minHeight: 0,
    [theme.breakpoints.up('lg')]: {
      flex: '0 0 32%',
      height: 'auto',
      maxHeight: 168,
      alignSelf: 'center',
    },
  },
  pieLegend: {
    flex: '1 1 auto',
    borderCollapse: 'collapse',
    margin: 0,
    minWidth: 0,
    width: '100%',
  },
  pieLegendItem: {
    fontFamily: 'DINPro, "DIN Pro", sans-serif',
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '16px',
    color: '#262626',
  },
  pieLegendCell: {
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.background.grey}`,
    verticalAlign: 'middle',
    '&:last-child': {
      textAlign: 'right',
    },
    'tr:last-child &': {
      borderBottom: 'none',
    },
  },
  pieLegendLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  pieLegendSwatch: {
    flexShrink: 0,
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  pieLegendName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  pieLegendValue: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
}))

type PieLegendItem = {
  label: string
  color: string
  percent: string
}

/** Builds pie legend items from chart config */
function buildPieLegendItems(chart: AccidentAtlasChartConfig): PieLegendItem[] {
  if (!isPieChartConfig(chart)) return []

  return buildPieDataTable(chart).map((row, index) => ({
    label: row.label,
    color: getChartColor(index),
    percent: row.percent,
  }))
}

function getChartTypeMessageId(
  renderType: AccidentAtlasChartRenderType
): string {
  switch (renderType) {
    case 'ColumnChart':
      return 'accidentAtlas.chart.type.column'
    case 'LineChart':
      return 'accidentAtlas.chart.type.line'
    case 'PieChart':
      return 'accidentAtlas.chart.type.pie'
    default:
      return 'accidentAtlas.chart.type.column'
  }
}

type AccidentAtlasChartProps = {
  chart: AccidentAtlasChartConfig
  renderType: AccidentAtlasChartRenderType
  className?: string
  /** Stable id for aria references */
  chartId: string
  /** Visible tile headline element id for aria-labelledby */
  captionHeadlineId?: string
  /** Headline text for summaries and table captions */
  accessibleTitle?: string
  /** Only mount when the tile face is visible (flip animation) */
  active?: boolean
  /** Current page for paginated column charts (controlled by tile shell) */
  page?: number
  /** Whether column chart data is paginated */
  shouldPaginate?: boolean
}

export default function AccidentAtlasChartComponent({
  chart,
  renderType,
  className,
  chartId,
  captionHeadlineId,
  accessibleTitle,
  active = true,
  page = 0,
  shouldPaginate = false,
}: AccidentAtlasChartProps): ReactElement | null {
  const classes = useStyles()
  const intl = useIntl()
  const prefersReducedMotion = usePrefersReducedMotion()
  const [mounted, setMounted] = useState(false)
  const descriptionId = `${chartId}-description`
  const figureLabelIds = [captionHeadlineId, descriptionId]
    .filter(Boolean)
    .join(' ')

  useEffect(() => {
    setMounted(true)
  }, [])

  const {
    pieConfig,
    barConfig,
    lineConfig,
    pieLegendItems,
    cartesianDataTable,
    cartesianSummary,
  } = useMemo(() => {
    if (renderType === 'PieChart' && isPieChartConfig(chart)) {
      return {
        pieConfig: chartToPieChartJsConfig(chart, {
          reducedMotion: prefersReducedMotion,
        }),
        barConfig: null,
        lineConfig: null,
        pieLegendItems: buildPieLegendItems(chart),
        cartesianDataTable: null,
        cartesianSummary: null,
      }
    }

    if (renderType === 'ColumnChart' && isCartesianChartConfig(chart)) {
      const yMax = shouldPaginate ? getCartesianChartMax(chart) : undefined
      const { chart: slicedChart, startIndex } = shouldPaginate
        ? sliceCartesianChart(chart, page, CARTESIAN_CHART_PAGE_SIZE)
        : { chart, startIndex: 0 }
      const table = buildCartesianDataTable(slicedChart)

      return {
        pieConfig: null,
        barConfig: chartToBarChartJsConfig(slicedChart, {
          yMax,
          colorOffset: startIndex,
          reducedMotion: prefersReducedMotion,
        }),
        lineConfig: null,
        pieLegendItems: [] as PieLegendItem[],
        cartesianDataTable: table,
        cartesianSummary: buildCartesianSummaryText(table, 'ColumnChart'),
      }
    }

    if (renderType === 'LineChart' && isCartesianChartConfig(chart)) {
      const table = buildCartesianDataTable(chart)

      return {
        pieConfig: null,
        barConfig: null,
        lineConfig: chartToLineChartJsConfig(chart, {
          reducedMotion: prefersReducedMotion,
        }),
        pieLegendItems: [] as PieLegendItem[],
        cartesianDataTable: table,
        cartesianSummary: buildCartesianSummaryText(table, 'LineChart'),
      }
    }

    return {
      pieConfig: null,
      barConfig: null,
      lineConfig: null,
      pieLegendItems: [] as PieLegendItem[],
      cartesianDataTable: null,
      cartesianSummary: null,
    }
  }, [chart, renderType, shouldPaginate, page, prefersReducedMotion])

  const chartTypeLabel = intl.formatMessage({
    id: getChartTypeMessageId(renderType),
  })

  const descriptionText = useMemo(() => {
    if (renderType === 'PieChart') {
      if (accessibleTitle) {
        return `${accessibleTitle}. ${chartTypeLabel}`
      }
      return chartTypeLabel
    }

    const summary = cartesianSummary
    if (!summary) return chartTypeLabel
    if (accessibleTitle) {
      return `${accessibleTitle}. ${chartTypeLabel}: ${summary}`
    }
    return `${chartTypeLabel}: ${summary}`
  }, [accessibleTitle, cartesianSummary, chartTypeLabel, renderType])

  const dataTableCaption = accessibleTitle
    ? intl.formatMessage(
        { id: 'accidentAtlas.chart.dataTable.caption' },
        { title: accessibleTitle }
      )
    : intl.formatMessage({
        id: 'accidentAtlas.chart.dataTable.captionFallback',
      })

  if (!mounted || !active) {
    return (
      <div
        className={clsx(
          renderType === 'PieChart' ? classes.pieRoot : classes.root,
          className
        )}
        aria-hidden
      />
    )
  }

  if (renderType === 'PieChart' && pieConfig) {
    return (
      <figure
        id={chartId}
        className={clsx(classes.figureRoot, className)}
        aria-labelledby={figureLabelIds || undefined}
      >
        <figcaption id={descriptionId} className={classes.visuallyHidden}>
          {descriptionText}
        </figcaption>
        <div className={classes.pieRoot}>
          <div className={classes.pieChart} aria-hidden="true">
            <Pie data={pieConfig.data} options={pieConfig.options} />
          </div>
          <table
            className={classes.pieLegend}
            aria-label={intl.formatMessage({
              id: 'accidentAtlas.chart.pie.legend.aria.label',
            })}
          >
            <thead className={classes.visuallyHidden}>
              <tr>
                <th scope="col">
                  {intl.formatMessage({
                    id: 'accidentAtlas.chart.pie.legend.category',
                  })}
                </th>
                <th scope="col">
                  {intl.formatMessage({
                    id: 'accidentAtlas.chart.pie.legend.share',
                  })}
                </th>
              </tr>
            </thead>
            <tbody>
              {pieLegendItems.map((item) => (
                <tr
                  key={item.label}
                  className={classes.pieLegendItem}
                  title={item.label}
                >
                  <td className={classes.pieLegendCell}>
                    <span className={classes.pieLegendLabel}>
                      <span
                        className={classes.pieLegendSwatch}
                        style={{ backgroundColor: item.color }}
                        aria-hidden="true"
                      />
                      <span className={classes.pieLegendName}>
                        {item.label}
                      </span>
                    </span>
                  </td>
                  <td
                    className={clsx(
                      classes.pieLegendCell,
                      classes.pieLegendValue
                    )}
                  >
                    {item.percent}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </figure>
    )
  }

  if (renderType === 'LineChart' && lineConfig && cartesianDataTable) {
    return (
      <figure
        id={chartId}
        className={clsx(classes.figureRoot, className)}
        aria-labelledby={figureLabelIds || undefined}
      >
        <figcaption id={descriptionId} className={classes.visuallyHidden}>
          {descriptionText}
        </figcaption>
        <div className={classes.root} aria-hidden="true">
          <Line data={lineConfig.data} options={lineConfig.options} />
        </div>
        <ChartDataTable table={cartesianDataTable} caption={dataTableCaption} />
      </figure>
    )
  }

  if (renderType === 'ColumnChart' && barConfig && cartesianDataTable) {
    return (
      <figure
        id={chartId}
        className={clsx(classes.figureRoot, className)}
        aria-labelledby={figureLabelIds || undefined}
      >
        <figcaption id={descriptionId} className={classes.visuallyHidden}>
          {descriptionText}
        </figcaption>
        <div className={classes.root} aria-hidden="true">
          <Bar data={barConfig.data} options={barConfig.options} />
        </div>
        <ChartDataTable table={cartesianDataTable} caption={dataTableCaption} />
      </figure>
    )
  }

  return null
}
